import { useState } from 'react';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from '../Card';
import { Input, InputWrapper, Label, TextArea } from '../Input';
import {
  useBrandContext,
  SectionGrid,
  FormGroup,
  SectionHeaderRow,
  AiFillButton,
  TagInput,
  Tag,
  TagRemove,
  TagInputField,
  SubSection,
  SubSectionTitle,
  SliderRow,
  SliderLabelRow,
  SliderLabel,
  SliderHint,
  SliderValue,
  RangeSlider,
  TotalAdvisory,
  Advanced,
  makeTagKeyDown,
} from './brandShared';

// Tab "Voice & Content": Voice Guide + Content Strategy (con su Content Mix).
// Lee/escribe todo a través de form (useBrandForm) vía useBrandContext.
export function BrandTabVoiceContent() {
  const { form } = useBrandContext();
  const { formData } = form;
  const voice = formData.voiceGuide;
  const mix = formData.contentMix;

  // Inputs locales de los tag-fields (el valor "en vuelo" antes de Enter/coma).
  const [wordsToUseInput, setWordsToUseInput] = useState('');
  const [wordsToAvoidInput, setWordsToAvoidInput] = useState('');
  const [topicsInput, setTopicsInput] = useState('');
  const [keywordsInput, setKeywordsInput] = useState('');

  // Total del content mix — válido solo cuando suma 100 (advisory, no bloqueante).
  const contentMixTotal =
    (mix?.education || 0) + (mix?.connection || 0) + (mix?.proof || 0) + (mix?.promotion || 0);

  return (
    <SectionGrid>
      {/* ─── Voice Guide ─────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <SectionHeaderRow>
            <div>
              <CardTitle>Brand Voice Guide</CardTitle>
              <CardDescription>How your brand communicates</CardDescription>
            </div>
            <AiFillButton type="button" onClick={() => form.openAiFill('voice')} title="Search with AI">
              <span aria-hidden="true">✨</span> AI
            </AiFillButton>
          </SectionHeaderRow>
        </CardHeader>
        <CardContent>
          <FormGroup>
            <InputWrapper>
              <Label htmlFor="voice-purpose">Voice Purpose</Label>
              <TextArea
                id="voice-purpose"
                value={voice?.purpose || ''}
                onChange={(e) => form.setVoiceGuideField('purpose', e.target.value)}
                placeholder="e.g., To educate our audience about skincare science in an approachable way, build trust, and position us as the go-to resource for clean beauty."
                rows={2}
              />
            </InputWrapper>
          </FormGroup>

          <FormGroup>
            <InputWrapper>
              <Label htmlFor="voice-tone">Voice Tone</Label>
              <Input
                id="voice-tone"
                value={voice?.tone || ''}
                onChange={(e) => form.setVoiceGuideField('tone', e.target.value)}
                placeholder="e.g., Professional yet warm, Conversational, Witty"
              />
            </InputWrapper>
          </FormGroup>

          <FormGroup>
            <InputWrapper>
              <Label htmlFor="voice-emotional-tone">Emotional Tone</Label>
              <Input
                id="voice-emotional-tone"
                value={voice?.emotionalTone || ''}
                onChange={(e) => form.setVoiceGuideField('emotionalTone', e.target.value)}
                placeholder="e.g., Empowering, Reassuring, Inspiring confidence"
              />
            </InputWrapper>
          </FormGroup>

          <FormGroup>
            <InputWrapper>
              <Label htmlFor="voice-character">Voice Character</Label>
              <Input
                id="voice-character"
                value={voice?.character || ''}
                onChange={(e) => form.setVoiceGuideField('character', e.target.value)}
                placeholder="e.g., Knowledgeable friend, Trusted advisor, Coach"
              />
            </InputWrapper>
          </FormGroup>

          <FormGroup>
            <InputWrapper>
              <Label htmlFor="voice-words-to-use">Words to Use (press Enter to add)</Label>
              <TagInput>
                {voice?.wordsToUse?.map((item, i) => (
                  <Tag key={i}>
                    {item}
                    <TagRemove
                      type="button"
                      aria-label={`Remove ${item}`}
                      onClick={() => form.removeVoiceTag('wordsToUse', i)}
                    >
                      <span aria-hidden="true">×</span>
                    </TagRemove>
                  </Tag>
                ))}
                <TagInputField
                  id="voice-words-to-use"
                  value={wordsToUseInput}
                  onChange={(e) => setWordsToUseInput(e.target.value)}
                  onKeyDown={makeTagKeyDown(
                    (v) => form.addVoiceTag('wordsToUse', v),
                    wordsToUseInput,
                    () => setWordsToUseInput(''),
                  )}
                  placeholder="e.g., Glow, Nourish, Radiant..."
                />
              </TagInput>
            </InputWrapper>
          </FormGroup>

          <FormGroup>
            <InputWrapper>
              <Label htmlFor="voice-words-to-avoid">Words to Avoid (press Enter to add)</Label>
              <TagInput>
                {voice?.wordsToAvoid?.map((item, i) => (
                  <Tag key={i}>
                    {item}
                    <TagRemove
                      type="button"
                      aria-label={`Remove ${item}`}
                      onClick={() => form.removeVoiceTag('wordsToAvoid', i)}
                    >
                      <span aria-hidden="true">×</span>
                    </TagRemove>
                  </Tag>
                ))}
                <TagInputField
                  id="voice-words-to-avoid"
                  value={wordsToAvoidInput}
                  onChange={(e) => setWordsToAvoidInput(e.target.value)}
                  onKeyDown={makeTagKeyDown(
                    (v) => form.addVoiceTag('wordsToAvoid', v),
                    wordsToAvoidInput,
                    () => setWordsToAvoidInput(''),
                  )}
                  placeholder="e.g., Cheap, Miracle, Anti-aging..."
                />
              </TagInput>
            </InputWrapper>
          </FormGroup>

          {/* Campos avanzados/orphan del voiceGuide: audience, syntax, language. */}
          <Advanced>
            <summary>Advanced voice settings</summary>
            <div>
              <FormGroup>
                <InputWrapper>
                  <Label htmlFor="voice-audience">Audience</Label>
                  <TextArea
                    id="voice-audience"
                    value={voice?.audience || ''}
                    onChange={(e) => form.setVoiceGuideField('audience', e.target.value)}
                    placeholder="e.g., Speaks to busy professionals who want fast, science-backed answers without jargon."
                    rows={2}
                  />
                </InputWrapper>
              </FormGroup>

              <FormGroup>
                <InputWrapper>
                  <Label htmlFor="voice-syntax">Syntax</Label>
                  <Input
                    id="voice-syntax"
                    value={voice?.syntax || ''}
                    onChange={(e) => form.setVoiceGuideField('syntax', e.target.value)}
                    placeholder="e.g., Short sentences, active voice, minimal jargon"
                  />
                </InputWrapper>
              </FormGroup>

              <FormGroup>
                <InputWrapper>
                  <Label htmlFor="voice-language">Language</Label>
                  <Input
                    id="voice-language"
                    value={voice?.language || ''}
                    onChange={(e) => form.setVoiceGuideField('language', e.target.value)}
                    placeholder="e.g., English (US), Spanish, bilingual"
                  />
                </InputWrapper>
              </FormGroup>
            </div>
          </Advanced>
        </CardContent>
      </Card>

      {/* ─── Content Strategy ────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <SectionHeaderRow>
            <div>
              <CardTitle>Content Strategy</CardTitle>
              <CardDescription>Topics and content mix for posts</CardDescription>
            </div>
            <AiFillButton type="button" onClick={() => form.openAiFill('content')} title="Search with AI">
              <span aria-hidden="true">✨</span> AI
            </AiFillButton>
          </SectionHeaderRow>
        </CardHeader>
        <CardContent>
          <FormGroup>
            <InputWrapper>
              <Label htmlFor="content-post-topics">Post Topics (press Enter to add)</Label>
              <TagInput>
                {formData.postTopics?.map((item, i) => (
                  <Tag key={i}>
                    {item}
                    <TagRemove
                      type="button"
                      aria-label={`Remove ${item}`}
                      onClick={() => form.removeTag('postTopics', i)}
                    >
                      <span aria-hidden="true">×</span>
                    </TagRemove>
                  </Tag>
                ))}
                <TagInputField
                  id="content-post-topics"
                  value={topicsInput}
                  onChange={(e) => setTopicsInput(e.target.value)}
                  onKeyDown={makeTagKeyDown(
                    (v) => form.addTag('postTopics', v),
                    topicsInput,
                    () => setTopicsInput(''),
                  )}
                  placeholder="Add topic (e.g., Mold Remediation, Air Quality)..."
                />
              </TagInput>
            </InputWrapper>
          </FormGroup>

          <FormGroup>
            <InputWrapper>
              <Label htmlFor="content-keywords">Keywords (press Enter to add)</Label>
              <TagInput>
                {formData.keywords?.map((item, i) => (
                  <Tag key={i}>
                    {item}
                    <TagRemove
                      type="button"
                      aria-label={`Remove ${item}`}
                      onClick={() => form.removeTag('keywords', i)}
                    >
                      <span aria-hidden="true">×</span>
                    </TagRemove>
                  </Tag>
                ))}
                <TagInputField
                  id="content-keywords"
                  value={keywordsInput}
                  onChange={(e) => setKeywordsInput(e.target.value)}
                  onKeyDown={makeTagKeyDown(
                    (v) => form.addTag('keywords', v),
                    keywordsInput,
                    () => setKeywordsInput(''),
                  )}
                  placeholder="e.g., clean beauty, skincare..."
                />
              </TagInput>
            </InputWrapper>
          </FormGroup>

          {/* Content Mix — 4 sliders 0-100 step 5; el total ideal es 100 (advisory). */}
          <SubSection>
            <SubSectionTitle>Content Mix (%)</SubSectionTitle>

            <SliderRow>
              <SliderLabelRow>
                <div>
                  <SliderLabel>Value &amp; Education</SliderLabel>
                  <SliderHint> — Tips, how-tos, industry insights</SliderHint>
                </div>
                <SliderValue>{mix?.education || 0}%</SliderValue>
              </SliderLabelRow>
              <RangeSlider
                type="range"
                min={0}
                max={100}
                step={5}
                aria-label="Value & Education %"
                value={mix?.education || 0}
                onChange={(e) => form.setContentMix('education', parseInt(e.target.value, 10) || 0)}
              />
            </SliderRow>

            <SliderRow>
              <SliderLabelRow>
                <div>
                  <SliderLabel>Connection &amp; Story</SliderLabel>
                  <SliderHint> — Behind-the-scenes, community, engagement</SliderHint>
                </div>
                <SliderValue>{mix?.connection || 0}%</SliderValue>
              </SliderLabelRow>
              <RangeSlider
                type="range"
                min={0}
                max={100}
                step={5}
                aria-label="Connection & Story %"
                value={mix?.connection || 0}
                onChange={(e) => form.setContentMix('connection', parseInt(e.target.value, 10) || 0)}
              />
            </SliderRow>

            <SliderRow>
              <SliderLabelRow>
                <div>
                  <SliderLabel>Proof &amp; Authority</SliderLabel>
                  <SliderHint> — Testimonials, case studies, results</SliderHint>
                </div>
                <SliderValue>{mix?.proof || 0}%</SliderValue>
              </SliderLabelRow>
              <RangeSlider
                type="range"
                min={0}
                max={100}
                step={5}
                aria-label="Proof & Authority %"
                value={mix?.proof || 0}
                onChange={(e) => form.setContentMix('proof', parseInt(e.target.value, 10) || 0)}
              />
            </SliderRow>

            <SliderRow>
              <SliderLabelRow>
                <div>
                  <SliderLabel>Direct Promotion</SliderLabel>
                  <SliderHint> — Sales, offers, CTAs, product highlights</SliderHint>
                </div>
                <SliderValue>{mix?.promotion || 0}%</SliderValue>
              </SliderLabelRow>
              <RangeSlider
                type="range"
                min={0}
                max={100}
                step={5}
                aria-label="Direct Promotion %"
                value={mix?.promotion || 0}
                onChange={(e) => form.setContentMix('promotion', parseInt(e.target.value, 10) || 0)}
              />
            </SliderRow>

            <TotalAdvisory role="status" aria-live="polite" $isValid={contentMixTotal === 100}>
              Total: {contentMixTotal}% {contentMixTotal !== 100 && '(should be 100%)'}
            </TotalAdvisory>
          </SubSection>
        </CardContent>
      </Card>
    </SectionGrid>
  );
}

export default BrandTabVoiceContent;
